'use client'

import { parseOptions } from '@/feature/cms/helpers/parser'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Picture } from '@/ui/media/picture/Picture.client'
import { generatePictureSource } from '@/ui/media/picture/Picture.service'
import { Table } from '@/ui/tables/table/Table.client'
import { cva } from 'class-variance-authority'
import parse from 'html-react-parser'
import type { CMSSimpleTableSchemaType } from '../cms-simple-table/CMSSimpleTable.schema'

export type CMSTableProps = UiComponentProps<CMSSimpleTableSchemaType>

// Cloned from CMSSimpleTable.server.tsx - a UI component can't import from a sibling UI component,
// only a shared gateway file could, and none exists for these tokens
const cmsTableTints = {
  transparent: 'border-none',
  primary: 'border-b-0 bg-primary text-white first:border-l-0 last:border-r-0',
  white: 'bg-white',
  'body-alt': 'bg-brand-2-50',
  'brand-2-800': 'bg-brand-2-800 text-white',
}

const cmsTableAligns = {
  left: 'items-start',
  center: 'items-center text-center',
  right: 'items-end',
}

const cmsTableVerticalAligns = {
  top: 'justify-start',
  middle: 'justify-center',
  bottom: 'justify-end',
}

const cmsTableFontWeights = {
  normal: 'font-normal',
  bold: 'font-bold',
}

const cmsTableVariants = cva('', {
  variants: {
    tint: cmsTableTints,
    align: cmsTableAligns,
    verticalAlign: cmsTableVerticalAligns,
    fontWeight: cmsTableFontWeights,
  },
  defaultVariants: {
    tint: 'white',
    align: 'left',
    verticalAlign: 'middle',
    fontWeight: 'normal',
  },
})

export function CMSTable({
  rows,
  overflowing = false,
  className,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: CMSTableProps) {
  const { t } = useTranslation(['cmsTable'])
  const [headersColumns, ...bodyRows] = rows.map((row) => row.columns)
  const bodyItems = bodyRows.map((cols, idx) => ({ id: `row-${idx}`, columns: cols }))

  const table = (
    <Table
      aria-label={ariaLabel ?? t('cmsTable:defaultAriaLabel')}
      separators
      variant="primary"
      layout="fixed"
      {...(!overflowing && { id, 'data-testid': dataTestId })}
      className={twMerge(!overflowing && className)}
    >
      <Table.Header columns={headersColumns}>
        {headersColumns?.map((col, idx) => (
          <Table.Column key={`col-${col.content}`} id={`col-${idx}`} isRowHeader={idx === 0}>
            {parse(col.content ?? '', parseOptions)}
          </Table.Column>
        ))}
      </Table.Header>

      <Table.Body items={bodyItems}>
        {(item) => (
          <Table.Row columns={headersColumns} id={item.id}>
            {headersColumns?.map((it, idx) => {
              const elt = item.columns?.[idx]

              return (
                <Table.Cell
                  key={`cell-${it.content}`}
                  className={cmsTableVariants({
                    align: elt?.align,
                    verticalAlign: elt?.verticalAlign,
                    fontWeight: elt?.fontWeight,
                    tint: elt?.tint,
                  })}
                >
                  {elt?.picture && (
                    <div className="flex w-full justify-center">
                      <Picture
                        pictureClassName="mx-auto"
                        image={generatePictureSource({
                          mobileUrl: elt.picture.path,
                          alt: elt.picture.alt,
                        })}
                      />
                    </div>
                  )}
                  {elt?.content && <div>{parse(elt.content, parseOptions)}</div>}
                </Table.Cell>
              )
            })}
          </Table.Row>
        )}
      </Table.Body>
    </Table>
  )

  if (overflowing) {
    return (
      <div
        id={id}
        data-testid={dataTestId ?? 'cms-table/scroll-wrapper'}
        className={twMerge('overflow-x-auto', className)}
      >
        {table}
      </div>
    )
  }

  return table
}
